// src/schemas/quota.ts
import { z } from "zod";
var QuotaUsageDto = z.object({
  /** Number of analyses consumed in the current window. */
  used: z.number().int().min(0),
  /** Maximum analyses allowed in the window (tier-dependent). */
  limit: z.number().int().min(0),
  /** Remaining = limit - used (convenience, always consistent). */
  remaining: z.number().int().min(0),
  /** ISO 8601 timestamp when the window resets. */
  resetAt: z.iso.datetime({ offset: true }),
  /** 'daily' for tiered users, 'weekly' for anonymous. */
  window: z.enum(["daily", "weekly"]),
  /** Source of the quota pool this user counts against. */
  source: z.enum(["individual", "family"]),
  /** Tier identifier (free, drama_plus, dramax, anonymous). */
  tier: z.enum(["anonymous", "free", "drama_plus", "dramax"])
});
var AnonymousQuotaDto = z.object({
  used: z.number().int().min(0),
  limit: z.number().int().min(0),
  remaining: z.number().int().min(0),
  resetAt: z.iso.datetime({ offset: true }),
  window: z.literal("weekly"),
  source: z.literal("device"),
  tier: z.literal("anonymous")
});
var FamilyQuotaDto = z.object({
  used: z.number().int().min(0),
  limit: z.number().int().min(0),
  remaining: z.number().int().min(0),
  resetAt: z.iso.datetime({ offset: true }),
  window: z.literal("daily"),
  source: z.literal("family"),
  tier: z.enum(["drama_plus", "dramax"]),
  familyPlanId: z.uuid()
});

// src/schemas/entitlements.ts
import { z as z2 } from "zod";
var QuotaPolicyDto = z2.object({
  /** Bucket the quota counts against. */
  scope: z2.enum(["user", "family", "device"]),
  /** ID for the scope bucket: user_id, family_plan_id, or device_id. */
  scopeId: z2.string(),
  /** Reset cadence for the quota window. */
  window: z2.enum(["daily", "weekly"]),
  /** Maximum analyses per window (tier-dependent). */
  limit: z2.number().int().min(0)
});
var EntitlementsDto = z2.object({
  /** Canonical tier identifier. */
  tier: z2.enum(["anonymous", "free", "drama_plus", "dramax"]),
  /** Visual layer for tier-specific UI (maps to tier level 1/2/3). */
  visualLayer: z2.enum(["layer1", "layer2", "layer3"]),
  /** Whether the tier comes from individual plan, family plan, or anonymous default. */
  source: z2.enum(["individual", "family", "anonymous"]),
  /** Family plan ID when `source === 'family'`, else null. */
  familyPlanId: z2.uuid().nullable(),
  /** True when the caller owns the family plan. */
  isFamilyAdmin: z2.boolean(),
  /** Max input message length (tier-dependent). */
  characterLimit: z2.number().int().min(0),
  /** Quota policy consumed by `useQuota` / `get-quota`. */
  quotaPolicy: QuotaPolicyDto,
  /** ISO 8601 timestamp of the last entitlement mutation. */
  updatedAt: z2.iso.datetime({ offset: true })
});

// src/schemas/tier.ts
import { z as z3 } from "zod";
var TierIdEnum = z3.enum(["anonymous", "free", "drama_plus", "dramax"]);
var VisualLayerEnum = z3.enum(["layer1", "layer2", "layer3"]);
var TierSourceEnum = z3.enum(["individual", "family", "anonymous"]);
var TierDto = z3.object({
  /** Canonical tier identifier. */
  tier: TierIdEnum,
  /** Visual layer (layer1|layer2|layer3) used for styling. */
  visualLayer: VisualLayerEnum,
  /** Source of the tier assignment. */
  source: TierSourceEnum,
  /** Family plan ID if source=family, otherwise null. */
  familyPlanId: z3.uuid().nullable(),
  /** True if this user is the admin of their family plan. */
  isFamilyAdmin: z3.boolean(),
  /** Daily analysis limit for this tier (weekly limit for anonymous). */
  dailyLimit: z3.number().int().min(0),
  /** ISO 8601 timestamp of the last tier-data update. */
  updatedAt: z3.iso.datetime({ offset: true })
});

// src/schemas/family-plan.ts
import { z as z6 } from "zod";

// db-truth/enums.ts
import { z as z4 } from "zod";
var _ANON_MIGRATION_MAP_STATUS_VALUES = ["active", "merged", "abandoned"];
var Db_AnonMigrationMapStatusSchema = z4.enum(_ANON_MIGRATION_MAP_STATUS_VALUES);
var AGENT_EXECUTION_FAILURES_FAILURE_STAGE_VALUES = ["pre_execution", "post_execution", "execution"];
var DbAgentExecutionFailuresFailureStageSchema = z4.enum(AGENT_EXECUTION_FAILURES_FAILURE_STAGE_VALUES);
var AGENT_EXECUTION_FAILURES_SEVERITY_VALUES = ["blocking", "warning"];
var DbAgentExecutionFailuresSeveritySchema = z4.enum(AGENT_EXECUTION_FAILURES_SEVERITY_VALUES);
var AGENT_EXECUTIONS_AGENT_TYPE_VALUES = ["drama_scoring", "share_copy_generation", "tooltip_generation", "pattern_detection", "rewrite_generation", "microlesson_generation", "archetype_recommendation", "persona_profiling", "viral_hook_generation", "transformation_insights", "pattern_library_updates", "cross_lingual_content", "lesson_summary_generation", "contextual_education", "adaptive_learning", "orchestration", "content_generation"];
var DbAgentExecutionsAgentTypeSchema = z4.enum(AGENT_EXECUTIONS_AGENT_TYPE_VALUES);
var AGENT_EXECUTIONS_PROVIDER_VALUES = ["groq", "openai", "anthropic", "google", "safety"];
var DbAgentExecutionsProviderSchema = z4.enum(AGENT_EXECUTIONS_PROVIDER_VALUES);
var AGENT_EXECUTIONS_USER_FEEDBACK_RATING_VALUES = ["positive", "negative"];
var DbAgentExecutionsUserFeedbackRatingSchema = z4.enum(AGENT_EXECUTIONS_USER_FEEDBACK_RATING_VALUES);
var AGENT_EXECUTIONS_USER_TIER_VALUES = ["anonymous", "free", "drama_plus", "dramax"];
var DbAgentExecutionsUserTierSchema = z4.enum(AGENT_EXECUTIONS_USER_TIER_VALUES);
var ANALYSIS_APPROVALS_STATUS_VALUES = ["pending", "approved", "rejected"];
var DbAnalysisApprovalsStatusSchema = z4.enum(ANALYSIS_APPROVALS_STATUS_VALUES);
var ANALYSIS_CACHE_CATEGORY_VALUES = ["safe", "caution", "high_drama", "extreme_drama"];
var DbAnalysisCacheCategorySchema = z4.enum(ANALYSIS_CACHE_CATEGORY_VALUES);
var ANALYSIS_CACHE_PROVIDER_VALUES = ["groq", "openai", "anthropic", "google", "safety"];
var DbAnalysisCacheProviderSchema = z4.enum(ANALYSIS_CACHE_PROVIDER_VALUES);
var ANALYSIS_HISTORY_CATEGORY_VALUES = ["safe", "caution", "high_drama", "extreme_drama"];
var DbAnalysisHistoryCategorySchema = z4.enum(ANALYSIS_HISTORY_CATEGORY_VALUES);
var ANALYSIS_HISTORY_DIMENSION_VALUES = ["temporal", "behavioral"];
var DbAnalysisHistoryDimensionSchema = z4.enum(ANALYSIS_HISTORY_DIMENSION_VALUES);
var ANALYSIS_HISTORY_PROVIDER_VALUES = ["groq", "openai", "anthropic", "google", "safety"];
var DbAnalysisHistoryProviderSchema = z4.enum(ANALYSIS_HISTORY_PROVIDER_VALUES);
var ANALYSIS_HISTORY_VISIBILITY_STATUS_VALUES = ["visible", "pending_approval", "approved", "hidden"];
var DbAnalysisHistoryVisibilityStatusSchema = z4.enum(ANALYSIS_HISTORY_VISIBILITY_STATUS_VALUES);
var ANALYSIS_REVISIONS_ORIGINAL_CATEGORY_VALUES = ["safe", "caution", "high_drama", "extreme_drama"];
var DbAnalysisRevisionsOriginalCategorySchema = z4.enum(ANALYSIS_REVISIONS_ORIGINAL_CATEGORY_VALUES);
var ANALYSIS_REVISIONS_STATUS_VALUES = ["in_progress", "completed", "abandoned"];
var DbAnalysisRevisionsStatusSchema = z4.enum(ANALYSIS_REVISIONS_STATUS_VALUES);
var ANALYSIS_REWRITE_EVENTS_DECISION_VALUES = ["accepted", "ignored", "modified"];
var DbAnalysisRewriteEventsDecisionSchema = z4.enum(ANALYSIS_REWRITE_EVENTS_DECISION_VALUES);
var ANALYSIS_REWRITE_EVENTS_SEVERITY_VALUES = ["low", "medium", "high"];
var DbAnalysisRewriteEventsSeveritySchema = z4.enum(ANALYSIS_REWRITE_EVENTS_SEVERITY_VALUES);
var ANONYMOUS_USAGE_PLATFORM_VALUES = ["ios", "android", "web"];
var DbAnonymousUsagePlatformSchema = z4.enum(ANONYMOUS_USAGE_PLATFORM_VALUES);
var BEHAVIOR_METRICS_TIME_WINDOW_VALUES = ["7d", "30d", "all"];
var DbBehaviorMetricsTimeWindowSchema = z4.enum(BEHAVIOR_METRICS_TIME_WINDOW_VALUES);
var BEHAVIOR_METRICS_TREND_VALUES = ["increasing", "decreasing", "stable"];
var DbBehaviorMetricsTrendSchema = z4.enum(BEHAVIOR_METRICS_TREND_VALUES);
var CACHE_INVALIDATION_QUEUE_INVALIDATION_TYPE_VALUES = ["profile", "quota", "family_tier", "family_members", "subscription", "tier_config", "practitioner_members"];
var DbCacheInvalidationQueueInvalidationTypeSchema = z4.enum(CACHE_INVALIDATION_QUEUE_INVALIDATION_TYPE_VALUES);
var CACHE_INVALIDATION_QUEUE_STATUS_VALUES = ["pending", "processing", "completed", "failed"];
var DbCacheInvalidationQueueStatusSchema = z4.enum(CACHE_INVALIDATION_QUEUE_STATUS_VALUES);
var USER_STATS_SUBSCRIPTION_TIER_VALUES = ["free", "drama_plus", "dramax", "anonymous", "premium", "enterprise"];
var DbUserStatsSubscriptionTierSchema = z4.enum(USER_STATS_SUBSCRIPTION_TIER_VALUES);
var CMS_EXPERIMENTS_STATUS_VALUES = ["draft", "running", "paused", "completed"];
var DbCmsExperimentsStatusSchema = z4.enum(CMS_EXPERIMENTS_STATUS_VALUES);
var CMS_FEATURES_CATEGORY_VALUES = ["analysis", "insights", "export", "support", "ai"];
var DbCmsFeaturesCategorySchema = z4.enum(CMS_FEATURES_CATEGORY_VALUES);
var CMS_PRICING_BILLING_INTERVAL_VALUES = ["week", "month", "quarter", "semester", "year"];
var DbCmsPricingBillingIntervalSchema = z4.enum(CMS_PRICING_BILLING_INTERVAL_VALUES);
var CMS_PRICING_PLAN_TYPE_VALUES = ["individual", "family"];
var DbCmsPricingPlanTypeSchema = z4.enum(CMS_PRICING_PLAN_TYPE_VALUES);
var CMS_QUIPS_CATEGORY_VALUES = ["pitching", "network", "budget", "talent", "trends", "meta", "user_focused", "dogfooding_family", "loading", "error", "success"];
var DbCmsQuipsCategorySchema = z4.enum(CMS_QUIPS_CATEGORY_VALUES);
var CMS_QUIPS_TONE_VALUES = ["playful", "insightful", "dramatic", "supportive"];
var DbCmsQuipsToneSchema = z4.enum(CMS_QUIPS_TONE_VALUES);
var CMS_UPSELL_MESSAGES_CONTEXT_VALUES = ["quota_exhausted", "quota_warning", "feature_gate", "upgrade_cta", "retention"];
var DbCmsUpsellMessagesContextSchema = z4.enum(CMS_UPSELL_MESSAGES_CONTEXT_VALUES);
var CMS_UPSELL_MESSAGES_URGENCY_VALUES = ["low", "medium", "high"];
var DbCmsUpsellMessagesUrgencySchema = z4.enum(CMS_UPSELL_MESSAGES_URGENCY_VALUES);
var COMMUNICATION_PATTERNS_PATTERN_TYPE_VALUES = ["aggressive", "passive", "assertive", "analytical"];
var DbCommunicationPatternsPatternTypeSchema = z4.enum(COMMUNICATION_PATTERNS_PATTERN_TYPE_VALUES);
var COMMUNICATION_PATTERNS_STATUS_VALUES = ["draft", "published", "archived"];
var DbCommunicationPatternsStatusSchema = z4.enum(COMMUNICATION_PATTERNS_STATUS_VALUES);
var CONTENT_FREE_ROUTING_AUDIT_EVENTS_ATTEMPT_CLASS_VALUES = ["initial", "retry"];
var DbContentFreeRoutingAuditEventsAttemptClassSchema = z4.enum(CONTENT_FREE_ROUTING_AUDIT_EVENTS_ATTEMPT_CLASS_VALUES);
var CONTENT_FREE_ROUTING_AUDIT_EVENTS_ENVIRONMENT_CLASS_VALUES = ["test", "staging"];
var DbContentFreeRoutingAuditEventsEnvironmentClassSchema = z4.enum(CONTENT_FREE_ROUTING_AUDIT_EVENTS_ENVIRONMENT_CLASS_VALUES);
var CONTENT_FREE_ROUTING_AUDIT_EVENTS_FEATURE_CLASS_VALUES = ["drama-analysis", "drama-rewrite"];
var DbContentFreeRoutingAuditEventsFeatureClassSchema = z4.enum(CONTENT_FREE_ROUTING_AUDIT_EVENTS_FEATURE_CLASS_VALUES);
var CONTENT_FREE_ROUTING_AUDIT_EVENTS_INVOCATION_IDENTITY_STATUS_VALUES = ["complete", "provider_metadata_missing", "provider_metadata_invalid", "synthetic_scope_required"];
var DbContentFreeRoutingAuditEventsInvocationIdentityStatusSchema = z4.enum(CONTENT_FREE_ROUTING_AUDIT_EVENTS_INVOCATION_IDENTITY_STATUS_VALUES);
var CONTENT_FREE_ROUTING_AUDIT_EVENTS_OUTCOME_CLASS_VALUES = ["shadow_eligible", "shadow_denied", "shadow_unavailable"];
var DbContentFreeRoutingAuditEventsOutcomeClassSchema = z4.enum(CONTENT_FREE_ROUTING_AUDIT_EVENTS_OUTCOME_CLASS_VALUES);
var CONTENT_TRANSLATIONS_CONTENT_TYPE_VALUES = ["archetype", "lesson", "pattern", "ui_string"];
var DbContentTranslationsContentTypeSchema = z4.enum(CONTENT_TRANSLATIONS_CONTENT_TYPE_VALUES);
var CONTENT_TRANSLATIONS_LOCALE_VALUES = ["en", "es", "de", "pt", "fr"];
var DbContentTranslationsLocaleSchema = z4.enum(CONTENT_TRANSLATIONS_LOCALE_VALUES);
var CONVERSATION_MEMBERS_ROLE_VALUES = ["admin", "member"];
var DbConversationMembersRoleSchema = z4.enum(CONVERSATION_MEMBERS_ROLE_VALUES);
var CONVERSATIONS_STATUS_VALUES = ["active", "archived", "deleted"];
var DbConversationsStatusSchema = z4.enum(CONVERSATIONS_STATUS_VALUES);
var CONVERSATIONS_TIER_REQUIRED_VALUES = ["free", "drama_plus", "dramax"];
var DbConversationsTierRequiredSchema = z4.enum(CONVERSATIONS_TIER_REQUIRED_VALUES);
var CRISIS_ASSISTANCE_REQUESTS_CRISIS_SEVERITY_VALUES = ["low", "medium", "high", "critical"];
var DbCrisisAssistanceRequestsCrisisSeveritySchema = z4.enum(CRISIS_ASSISTANCE_REQUESTS_CRISIS_SEVERITY_VALUES);
var CRISIS_ASSISTANCE_REQUESTS_STATUS_VALUES = ["pending", "viewed", "responded", "resolved", "expired"];
var DbCrisisAssistanceRequestsStatusSchema = z4.enum(CRISIS_ASSISTANCE_REQUESTS_STATUS_VALUES);
var CRISIS_ASSISTANCE_REQUESTS_URGENCY_LEVEL_VALUES = ["standard", "urgent", "critical"];
var DbCrisisAssistanceRequestsUrgencyLevelSchema = z4.enum(CRISIS_ASSISTANCE_REQUESTS_URGENCY_LEVEL_VALUES);
var CRISIS_DETECTION_FEEDBACK_FEEDBACK_SOURCE_VALUES = ["patient", "practitioner", "system"];
var DbCrisisDetectionFeedbackFeedbackSourceSchema = z4.enum(CRISIS_DETECTION_FEEDBACK_FEEDBACK_SOURCE_VALUES);
var CRISIS_DETECTION_FEEDBACK_SEVERITY_FEEDBACK_VALUES = ["too_low", "correct", "too_high"];
var DbCrisisDetectionFeedbackSeverityFeedbackSchema = z4.enum(CRISIS_DETECTION_FEEDBACK_SEVERITY_FEEDBACK_VALUES);
var DELETION_AUDIT_LOG_DELETION_TRIGGER_VALUES = ["auto_retention", "user_manual", "gdpr_request"];
var DbDeletionAuditLogDeletionTriggerSchema = z4.enum(DELETION_AUDIT_LOG_DELETION_TRIGGER_VALUES);
var ENGINE_DEFINITIONS_CATEGORY_VALUES = ["analysis", "rewrite", "insight", "detection", "generation"];
var DbEngineDefinitionsCategorySchema = z4.enum(ENGINE_DEFINITIONS_CATEGORY_VALUES);
var ENGINE_DEFINITIONS_TIER_REQUIRED_VALUES = ["anonymous", "free", "drama_plus", "dramax"];
var DbEngineDefinitionsTierRequiredSchema = z4.enum(ENGINE_DEFINITIONS_TIER_REQUIRED_VALUES);
var EXECUTION_INTENTS_SCOPE_KIND_VALUES = ["user", "family"];
var DbExecutionIntentsScopeKindSchema = z4.enum(EXECUTION_INTENTS_SCOPE_KIND_VALUES);
var EXECUTION_INTENTS_STATUS_VALUES = ["accepted", "reserved", "dispatched", "completed", "failed", "unknown"];
var DbExecutionIntentsStatusSchema = z4.enum(EXECUTION_INTENTS_STATUS_VALUES);
var FAMILY_INVITES_STATUS_VALUES = ["pending", "accepted", "expired", "cancelled"];
var DbFamilyInvitesStatusSchema = z4.enum(FAMILY_INVITES_STATUS_VALUES);
var FAMILY_PLANS_BILLING_INTERVAL_VALUES = ["weekly", "monthly", "quarterly", "semester", "yearly"];
var DbFamilyPlansBillingIntervalSchema = z4.enum(FAMILY_PLANS_BILLING_INTERVAL_VALUES);
var FAMILY_PLANS_STATUS_VALUES = ["active", "cancelled", "expired", "past_due"];
var DbFamilyPlansStatusSchema = z4.enum(FAMILY_PLANS_STATUS_VALUES);
var FAMILY_PLANS_TIER_VALUES = ["drama_plus", "dramax"];
var DbFamilyPlansTierSchema = z4.enum(FAMILY_PLANS_TIER_VALUES);
var GIFT_CARDS_STATUS_VALUES = ["pending", "code_generated", "notified", "unused", "redeemed", "expired", "canceled", "generation_failed"];
var DbGiftCardsStatusSchema = z4.enum(GIFT_CARDS_STATUS_VALUES);
var GUEST_PASSES_STATUS_VALUES = ["active", "expired", "converted", "revoked"];
var DbGuestPassesStatusSchema = z4.enum(GUEST_PASSES_STATUS_VALUES);
var GUEST_PASSES_TIER_GRANTED_VALUES = ["drama_plus", "dramax"];
var DbGuestPassesTierGrantedSchema = z4.enum(GUEST_PASSES_TIER_GRANTED_VALUES);
var ISSUE_TYPE_REGISTRY_TIER_VALUES = ["canonical", "provisional", "discovered"];
var DbIssueTypeRegistryTierSchema = z4.enum(ISSUE_TYPE_REGISTRY_TIER_VALUES);
var LLM_EXECUTION_LOGS_STATUS_VALUES = ["success", "error", "timeout"];
var DbLlmExecutionLogsStatusSchema = z4.enum(LLM_EXECUTION_LOGS_STATUS_VALUES);
var MESSAGES_IDEMPOTENCY_STATUS_VALUES = ["pending", "complete"];
var DbMessagesIdempotencyStatusSchema = z4.enum(MESSAGES_IDEMPOTENCY_STATUS_VALUES);
var MODERATION_EVENTS_DETECTION_SOURCE_VALUES = ["openai_moderation", "crisis_keywords", "user_flag", "user_action"];
var DbModerationEventsDetectionSourceSchema = z4.enum(MODERATION_EVENTS_DETECTION_SOURCE_VALUES);
var MODERATION_EVENTS_EVENT_TYPE_VALUES = ["crisis_detected", "self_harm_detected", "user_reported", "resources_shown", "hotline_clicked", "help_button_clicked", "tos_shown", "tos_accepted", "tos_declined"];
var DbModerationEventsEventTypeSchema = z4.enum(MODERATION_EVENTS_EVENT_TYPE_VALUES);
var MODERATION_EVENTS_REVIEW_ACTION_VALUES = ["dismissed", "escalated", "resolved"];
var DbModerationEventsReviewActionSchema = z4.enum(MODERATION_EVENTS_REVIEW_ACTION_VALUES);
var MODERATION_EVENTS_SEVERITY_VALUES = ["low", "medium", "high", "critical"];
var DbModerationEventsSeveritySchema = z4.enum(MODERATION_EVENTS_SEVERITY_VALUES);
var PAYMENTS_PAYMENT_TYPE_VALUES = ["subscription", "gift_card", "one_time"];
var DbPaymentsPaymentTypeSchema = z4.enum(PAYMENTS_PAYMENT_TYPE_VALUES);
var PAYMENTS_STATUS_VALUES = ["succeeded", "processing", "requires_payment_method", "requires_confirmation", "requires_action", "canceled", "failed"];
var DbPaymentsStatusSchema = z4.enum(PAYMENTS_STATUS_VALUES);
var PLUGIN_AUDIT_LOG_SEVERITY_VALUES = ["CRITICAL", "WARNING"];
var DbPluginAuditLogSeveritySchema = z4.enum(PLUGIN_AUDIT_LOG_SEVERITY_VALUES);
var PLUGIN_AUDIT_LOG_VIOLATION_TYPE_VALUES = ["import", "database", "redis", "qstash", "http", "env"];
var DbPluginAuditLogViolationTypeSchema = z4.enum(PLUGIN_AUDIT_LOG_VIOLATION_TYPE_VALUES);
var PRACTITIONER_CONSENT_CRISIS_NOTIFICATION_CONTACT_METHOD_VALUES = ["email", "push", "both"];
var DbPractitionerConsentCrisisNotificationContactMethodSchema = z4.enum(PRACTITIONER_CONSENT_CRISIS_NOTIFICATION_CONTACT_METHOD_VALUES);
var PRACTITIONER_CONSENT_CRISIS_NOTIFICATION_URGENCY_PREFERENCE_VALUES = ["all", "high_only", "critical_only"];
var DbPractitionerConsentCrisisNotificationUrgencyPreferenceSchema = z4.enum(PRACTITIONER_CONSENT_CRISIS_NOTIFICATION_URGENCY_PREFERENCE_VALUES);
var PRACTITIONER_GROUP_MEMBERSHIPS_ROLE_VALUES = ["owner", "member"];
var DbPractitionerGroupMembershipsRoleSchema = z4.enum(PRACTITIONER_GROUP_MEMBERSHIPS_ROLE_VALUES);
var PRACTITIONER_GROUPS_STATUS_VALUES = ["active", "cancelled", "expired"];
var DbPractitionerGroupsStatusSchema = z4.enum(PRACTITIONER_GROUPS_STATUS_VALUES);
var PRACTITIONER_INVITES_STATUS_VALUES = ["pending", "accepted", "expired", "cancelled"];
var DbPractitionerInvitesStatusSchema = z4.enum(PRACTITIONER_INVITES_STATUS_VALUES);
var PRACTITIONER_PROFILES_CERTIFICATION_STATUS_VALUES = ["pending", "certified", "suspended"];
var DbPractitionerProfilesCertificationStatusSchema = z4.enum(PRACTITIONER_PROFILES_CERTIFICATION_STATUS_VALUES);
var PRACTITIONER_PROFILES_PARTNER_TYPE_VALUES = ["practitioner", "coach", "influencer", "corporate", "other"];
var DbPractitionerProfilesPartnerTypeSchema = z4.enum(PRACTITIONER_PROFILES_PARTNER_TYPE_VALUES);
var PRACTITIONER_PROFILES_PRACTICE_TYPE_VALUES = ["therapist", "coach", "counselor", "other"];
var DbPractitionerProfilesPracticeTypeSchema = z4.enum(PRACTITIONER_PROFILES_PRACTICE_TYPE_VALUES);
var PRACTITIONER_PROFILES_STATUS_VALUES = ["invited", "applied", "approved", "rejected", "suspended"];
var DbPractitionerProfilesStatusSchema = z4.enum(PRACTITIONER_PROFILES_STATUS_VALUES);
var PROFILES_GROUP_TYPE_VALUES = ["family", "practitioner"];
var DbProfilesGroupTypeSchema = z4.enum(PROFILES_GROUP_TYPE_VALUES);
var PROFILES_LOCALE_VALUES = ["en", "es", "de", "pt", "fr"];
var DbProfilesLocaleSchema = z4.enum(PROFILES_LOCALE_VALUES);
var PROFILES_QUOTA_SOURCE_VALUES = ["individual", "family", "practitioner"];
var DbProfilesQuotaSourceSchema = z4.enum(PROFILES_QUOTA_SOURCE_VALUES);
var QSTASH_DLQ_PRIORITY_VALUES = ["critical", "high", "medium", "low"];
var DbQstashDlqPrioritySchema = z4.enum(QSTASH_DLQ_PRIORITY_VALUES);
var QSTASH_DLQ_STATUS_VALUES = ["pending", "reprocessed", "resolved", "ignored"];
var DbQstashDlqStatusSchema = z4.enum(QSTASH_DLQ_STATUS_VALUES);
var QUOTA_CHANGE_LOG_CHANGE_TYPE_VALUES = ["increment", "decrement", "reset", "manual_adjust", "tier_change", "family_join", "family_leave"];
var DbQuotaChangeLogChangeTypeSchema = z4.enum(QUOTA_CHANGE_LOG_CHANGE_TYPE_VALUES);
var QUOTA_CHANGE_LOG_SCOPE_VALUES = ["individual", "family", "practitioner"];
var DbQuotaChangeLogScopeSchema = z4.enum(QUOTA_CHANGE_LOG_SCOPE_VALUES);
var QUOTA_CHANGE_LOG_SOURCE_VALUES = ["redis", "postgres", "webhook", "admin", "cron"];
var DbQuotaChangeLogSourceSchema = z4.enum(QUOTA_CHANGE_LOG_SOURCE_VALUES);
var QUOTA_RESET_LOGS_RESET_TYPE_VALUES = ["daily", "monthly"];
var DbQuotaResetLogsResetTypeSchema = z4.enum(QUOTA_RESET_LOGS_RESET_TYPE_VALUES);
var QUOTA_USAGE_LEDGER_OPERATION_VALUES = ["consume", "grant", "expire", "refund", "transfer_in", "transfer_out"];
var DbQuotaUsageLedgerOperationSchema = z4.enum(QUOTA_USAGE_LEDGER_OPERATION_VALUES);
var QUOTA_USAGE_LEDGER_SCOPE_VALUES = ["user", "family", "practitioner", "anonymous"];
var DbQuotaUsageLedgerScopeSchema = z4.enum(QUOTA_USAGE_LEDGER_SCOPE_VALUES);
var QUOTA_USAGE_LEDGER_SOURCE_VALUES = ["analysis", "manual_grant", "subscription", "promo", "monthly_reset", "weekly_reset", "family_share", "practitioner_share", "error_refund"];
var DbQuotaUsageLedgerSourceSchema = z4.enum(QUOTA_USAGE_LEDGER_SOURCE_VALUES);
var REFERRAL_CODES_CODE_TYPE_VALUES = ["customer", "partner"];
var DbReferralCodesCodeTypeSchema = z4.enum(REFERRAL_CODES_CODE_TYPE_VALUES);
var REFERRAL_REWARDS_REWARD_TYPE_VALUES = ["guest_pass", "trial_extension", "quota_bonus", "commission"];
var DbReferralRewardsRewardTypeSchema = z4.enum(REFERRAL_REWARDS_REWARD_TYPE_VALUES);
var REFERRAL_REWARDS_STATUS_VALUES = ["pending", "issued", "redeemed", "expired", "revoked"];
var DbReferralRewardsStatusSchema = z4.enum(REFERRAL_REWARDS_STATUS_VALUES);
var REFERRALS_SOURCE_TYPE_VALUES = ["customer", "partner", "affiliate"];
var DbReferralsSourceTypeSchema = z4.enum(REFERRALS_SOURCE_TYPE_VALUES);
var REFERRALS_STATUS_VALUES = ["pending", "signed_up", "subscribed", "rewarded", "expired", "fraudulent"];
var DbReferralsStatusSchema = z4.enum(REFERRALS_STATUS_VALUES);
var SHARED_ANALYSIS_LINKS_LINK_TYPE_VALUES = ["legacy", "explicit_result"];
var DbSharedAnalysisLinksLinkTypeSchema = z4.enum(SHARED_ANALYSIS_LINKS_LINK_TYPE_VALUES);
var SUBSCRIPTION_CHANGE_REQUESTS_ACTION_VALUES = ["upgrade", "downgrade", "change_interval", "cancel", "reactivate"];
var DbSubscriptionChangeRequestsActionSchema = z4.enum(SUBSCRIPTION_CHANGE_REQUESTS_ACTION_VALUES);
var SUBSCRIPTION_CHANGE_REQUESTS_ENVIRONMENT_VALUES = ["sandbox", "production"];
var DbSubscriptionChangeRequestsEnvironmentSchema = z4.enum(SUBSCRIPTION_CHANGE_REQUESTS_ENVIRONMENT_VALUES);
var SUBSCRIPTION_CHANGE_REQUESTS_STATUS_VALUES = ["requested", "sent_to_polar", "applied", "failed", "superseded"];
var DbSubscriptionChangeRequestsStatusSchema = z4.enum(SUBSCRIPTION_CHANGE_REQUESTS_STATUS_VALUES);
var SUBSCRIPTIONS_STATUS_VALUES = ["active", "canceled", "past_due", "unpaid", "trialing", "incomplete", "incomplete_expired"];
var DbSubscriptionsStatusSchema = z4.enum(SUBSCRIPTIONS_STATUS_VALUES);
var SUBSCRIPTIONS_TIER_VALUES = ["free", "anonymous", "drama_plus", "dramax"];
var DbSubscriptionsTierSchema = z4.enum(SUBSCRIPTIONS_TIER_VALUES);
var TOS_ACCEPTANCE_ACCEPTANCE_METHOD_VALUES = ["checkbox", "modal", "signup"];
var DbTosAcceptanceAcceptanceMethodSchema = z4.enum(TOS_ACCEPTANCE_ACCEPTANCE_METHOD_VALUES);
var TRAINING_SESSIONS_STATUS_VALUES = ["active", "paused", "completed", "abandoned"];
var DbTrainingSessionsStatusSchema = z4.enum(TRAINING_SESSIONS_STATUS_VALUES);
var USER_CONTEXT_CACHE_TIER_VALUES = ["free", "drama_plus", "dramax"];
var DbUserContextCacheTierSchema = z4.enum(USER_CONTEXT_CACHE_TIER_VALUES);
var USER_REPORTS_PRIORITY_VALUES = ["low", "medium", "high", "urgent"];
var DbUserReportsPrioritySchema = z4.enum(USER_REPORTS_PRIORITY_VALUES);
var USER_REPORTS_REPORT_REASON_VALUES = ["self_harm", "crisis", "harmful_content", "other"];
var DbUserReportsReportReasonSchema = z4.enum(USER_REPORTS_REPORT_REASON_VALUES);
var USER_REPORTS_STATUS_VALUES = ["pending", "reviewing", "resolved", "dismissed"];
var DbUserReportsStatusSchema = z4.enum(USER_REPORTS_STATUS_VALUES);
var USER_STATS_STATUS_VALUES = ["active", "suspended", "canceled"];
var DbUserStatsStatusSchema = z4.enum(USER_STATS_STATUS_VALUES);

// src/schemas/_primitives.ts
import { z as z5 } from "zod";
var BetterAuthUserIdSchema = z5.string().min(1);

// src/schemas/family-plan.ts
var FamilyPlanDetailsDto = z6.object({
  /** Family plan UUID (primary key of the `family_plans` table). */
  familyPlanId: z6.uuid(),
  /** Subscribed tier (families are always drama_plus or dramax). */
  tier: z6.enum(["drama_plus", "dramax"]),
  /** Billing cadence reported by the provider. Uses LONG form (monthly, yearly) matching `family_plans` table. */
  billingInterval: z6.enum(FAMILY_PLANS_BILLING_INTERVAL_VALUES).nullable(),
  /** Subscription status of the family plan. */
  status: z6.enum(["active", "cancelled", "expired", "past_due"]),
  /** Admin user ID (owner of the plan) — Better Auth text ID, not UUID. */
  adminUserId: BetterAuthUserIdSchema,
  /** True if the current caller is the family admin. */
  isAdmin: z6.boolean(),
  /** Current member count (admin + members). */
  memberCount: z6.number().int().min(0),
  /** Shared daily analysis quota pool for the plan. */
  dailyQuota: z6.number().int().min(0),
  /** End of the current billing period, if known. */
  currentPeriodEnd: z6.iso.datetime({ offset: true }).nullable()
});
var FamilyMemberDto = z6.object({
  userId: BetterAuthUserIdSchema,
  email: z6.string(),
  displayName: z6.string().nullable(),
  isAdmin: z6.boolean(),
  joinedAt: z6.iso.datetime({ offset: true })
});
var FamilyInviteDto = z6.object({
  id: z6.uuid(),
  email: z6.string(),
  /** Short invite code used in deep links and `get_family_invite_details` lookups. */
  inviteCode: z6.string(),
  status: z6.enum(["pending", "accepted", "expired", "cancelled"]),
  createdAt: z6.iso.datetime({ offset: true }),
  expiresAt: z6.iso.datetime({ offset: true })
});
var FamilyPlanDto = z6.object({
  plan: FamilyPlanDetailsDto.nullable(),
  members: z6.array(FamilyMemberDto),
  invites: z6.array(FamilyInviteDto)
});
var FamilyPlanTierDtoEnum = z6.enum(["drama_plus", "dramax"]);
var FamilyInviteBillingIntervalEnum = z6.enum(["monthly", "yearly"]);
var FamilyInviteStatusEnum = z6.enum([
  "pending",
  "accepted",
  "expired",
  "cancelled"
]);
var FamilyInviteDetailsDto = z6.object({
  /** Invite row UUID. */
  id: z6.uuid(),
  /** UUID of the family plan the invite belongs to. */
  family_plan_id: z6.uuid(),
  /** Email the invite was sent to. */
  invited_email: z6.string(),
  /** Current lifecycle state. */
  status: FamilyInviteStatusEnum,
  /** ISO 8601 timestamp of invite creation. */
  created_at: z6.iso.datetime({ offset: true }),
  /** ISO 8601 timestamp at which the invite expires. */
  expires_at: z6.iso.datetime({ offset: true }),
  /** Snapshot of the family plan at invite time. */
  family_plan: z6.object({
    tier: FamilyPlanTierDtoEnum,
    billing_interval: FamilyInviteBillingIntervalEnum,
    daily_quota: z6.number().int().min(0),
    admin_user_id: BetterAuthUserIdSchema,
    /** Pre-computed display name of the admin (defaults to 'Family Admin'). */
    admin_name: z6.string().nullable(),
    member_count: z6.number().int().min(0)
  })
});
var FamilyInviteResultDto = z6.object({
  invite_id: z6.uuid(),
  invite_code: z6.string(),
  /** ISO 8601 timestamp at which the invite expires. */
  expires_at: z6.iso.datetime({ offset: true }),
  /** Email the invite was dispatched to. */
  invited_email: z6.string(),
  /** Deep link the recipient can use to accept the invite in the native app. */
  deep_link: z6.string(),
  /** HTTPS fallback URL the recipient can use on the web. */
  web_link: z6.string()
});
var FamilyAcceptResultDto = z6.object({
  /** UUID of the family plan the caller just joined. */
  family_plan_id: z6.uuid(),
  /** Resolved tier of the family plan (inherited by the new member). */
  tier: FamilyPlanTierDtoEnum,
  /** Admin of the plan (for follow-up notifications, analytics, etc.). */
  admin_user_id: BetterAuthUserIdSchema,
  /** True if the caller joined; false when the invite was already consumed. */
  accepted: z6.boolean()
});
var FamilyRemoveResultDto = z6.object({
  /** ID of the member that was removed (Better Auth text ID). */
  removed_user_id: BetterAuthUserIdSchema,
  /** Updated member count after the removal. */
  member_count: z6.number().int().min(0)
});
var FamilyCancelInviteResultDto = z6.object({
  cancelled: z6.literal(true)
});

// src/schemas/subscription.ts
import { z as z7 } from "zod";
var SubscriptionTierEnum = z7.enum(["free", "drama_plus", "dramax"]);
var SubscriptionStatusEnum = z7.enum([
  "active",
  "trialing",
  "past_due",
  "canceled",
  "cancelled",
  "expired",
  "unpaid",
  "incomplete",
  "incomplete_expired",
  "none"
]);
var SubscriptionStatusDto = z7.object({
  /** Canonical tier of the active subscription (or 'free' when none). */
  tier: SubscriptionTierEnum,
  /** Subscription status (or 'none' when the user has never subscribed). */
  status: SubscriptionStatusEnum,
  /** End of the current billing period (ISO 8601). */
  currentPeriodEnd: z7.iso.datetime({ offset: true }).nullable().optional(),
  /** True if a cancellation is scheduled at the end of the current period. */
  cancelAtPeriodEnd: z7.boolean().optional(),
  /** End of a free trial, if applicable (ISO 8601). */
  trialEnd: z7.iso.datetime({ offset: true }).nullable().optional(),
  /** Payment provider identifier ('polar' | 'stripe_legacy' | ...). */
  provider: z7.string().nullable().optional(),
  /** Provider customer id — retained even after cancellation for re-sub flows. */
  providerCustomerId: z7.string().nullable().optional(),
  /** Provider subscription id (null when status === 'none'). */
  providerSubscriptionId: z7.string().nullable().optional()
});
var CancelSubscriptionResultDto = z7.object({
  /** ISO 8601 end of the active period (user retains access until then). */
  activeUntil: z7.iso.datetime({ offset: true }).nullable(),
  /** True when the subscription was already cancel_at_period_end — no-op. */
  alreadyCancelled: z7.boolean().optional(),
  /**
   * True when Polar succeeded but the follow-up DB write failed. The cancel
   * is real in Polar; the UI should toast optimistically and rely on the next
   * poll / webhook to reconcile local state.
   */
  dbSyncPending: z7.boolean().optional()
});
var UpdateSubscriptionActionEnum = z7.enum([
  "upgrade",
  "downgrade",
  "change_interval"
]);
var UpdateSubscriptionResultDto = z7.object({
  /** Action that was applied. */
  action: UpdateSubscriptionActionEnum,
  /** Tier the subscription was on before the update. */
  previous_tier: z7.string(),
  /** Tier the subscription transitioned to. */
  new_tier: z7.string(),
  /** ISO 8601 timestamp at which the change takes effect. */
  effective_at: z7.iso.datetime({ offset: true }),
  /** True when Polar charged a proration invoice immediately. */
  prorated: z7.boolean(),
  /** ISO 8601 next billing date reported by Polar (or fallback). */
  next_billing_date: z7.iso.datetime({ offset: true }).nullable(),
  /** Amount of the next invoice (null when Polar did not return a value). */
  next_billing_amount: z7.number().nullable(),
  /**
   * True when Polar succeeded but the follow-up DB write failed. The change
   * is real in Polar; the client should refetch and reconcile locally.
   */
  dbSyncPending: z7.boolean().optional()
});
var CustomerPortalSessionResultDto = z7.object({
  /** HTTPS URL of the Polar-hosted customer portal session. */
  portal_url: z7.string().url()
});

// src/schemas/analysis-stats.ts
import { z as z8 } from "zod";
var AnalysisStatsDto = z8.object({
  /** Total number of analyses the caller has run. */
  totalAnalyses: z8.number().int().min(0),
  /** Mean drama score across all analyses, rounded to 2 decimals. */
  averageScore: z8.number().min(0),
  /** Count of analyses per category (free-text category string → count). */
  categoryBreakdown: z8.record(z8.string(), z8.number().int().min(0))
});

// src/schemas/analysis.ts
import { z as z9 } from "zod";
var AnalysisRecordCategoryEnum = z9.enum([
  "safe",
  "caution",
  "high_drama",
  "extreme_drama"
]);
var AnalysisRecordProviderEnum = z9.enum([
  "groq",
  "openai",
  "google",
  "anthropic",
  "safety"
]);
var AnalysisDto = z9.object({
  /** Row primary key. */
  id: z9.uuid(),
  /** Authenticated owner, null for anonymous rows. */
  user_id: BetterAuthUserIdSchema.nullable(),
  /** Device fingerprint for anonymous rows, null for authenticated rows. */
  device_id: z9.string().nullable().optional(),
  /** Analyzed message text (null if encrypted field failed to decrypt). */
  message: z9.string().nullable(),
  /** Optional message context tag (e.g. 'family', 'work'). */
  context: z9.string().nullable(),
  /** Drama score 0-10. */
  score: z9.number().min(0).max(10),
  /** Drama category — null for circuit-breaker / crisis-safe responses. */
  category: AnalysisRecordCategoryEnum.nullable(),
  /** Analytical summary (may be null if decryption failed). */
  summary: z9.string().nullable(),
  /** Narrator introduction (tier-dependent; may be null or absent). */
  narrator_intro: z9.string().nullable().optional(),
  /** Detected drama issues — opaque JSON validated at analyze time. */
  issues: z9.array(z9.unknown()).nullable(),
  /** Suggested rewrites — opaque JSON validated at analyze time. */
  rewrites: z9.array(z9.unknown()).nullable(),
  /** LLM provider that generated the row. */
  provider: AnalysisRecordProviderEnum.nullable(),
  /** LLM model identifier. */
  model: z9.string().nullable(),
  /** End-to-end pipeline latency for the analysis that produced this row. */
  latency_ms: z9.number().nullable(),
  /** Tokens consumed by the LLM call. */
  tokens_used: z9.number().nullable(),
  /** ISO 8601 timestamp of row creation. */
  created_at: z9.iso.datetime({ offset: true }),
  /** Whether `message` was stored encrypted (handler returns plaintext post-decrypt). */
  message_encrypted: z9.boolean().nullable().optional(),
  /** Whether `summary` was stored encrypted. */
  summary_encrypted: z9.boolean().nullable().optional(),
  /** Whether `context` was stored encrypted. */
  context_encrypted: z9.boolean().nullable().optional(),
  /** Whether `narrator_intro` was stored encrypted. */
  narrator_intro_encrypted: z9.boolean().nullable().optional(),
  /** Whether `issues` was stored encrypted. */
  issues_encrypted: z9.boolean().nullable().optional(),
  /** Whether `rewrites` was stored encrypted. */
  rewrites_encrypted: z9.boolean().nullable().optional(),
  /** Key version used for encryption (1 = shared, 2 = per-user). */
  encryption_key_version: z9.number().int().nullable().optional()
});
var DeleteAnalysisResultDto = z9.object({
  /** True when the row was deleted (or was already absent — idempotent). */
  deleted: z9.literal(true),
  /** Echo of the deleted row's ID. Optional for back-compat with older handlers. */
  deletedId: z9.uuid().optional()
});
var ClearAnalysisResultDto = z9.object({
  /** Number of rows deleted. Zero when the caller had no history. */
  deletedCount: z9.number().int().min(0)
});

// src/schemas/analysis-history.ts
import { z as z10 } from "zod";
var DramaCategoryEnum = z10.enum([
  "safe",
  "caution",
  "high_drama",
  "extreme_drama"
]);
var AnalysisProviderEnum = z10.enum([
  "groq",
  "openai",
  "google",
  "anthropic",
  "safety"
]);
var AnalysisHistoryEntryDto = z10.object({
  /** Row primary key. */
  id: z10.uuid(),
  /** Authenticated owner, null for anonymous rows. */
  user_id: BetterAuthUserIdSchema.nullable(),
  /** Device fingerprint for anonymous rows, null for authenticated rows. */
  device_id: z10.string().nullable(),
  /** Analyzed message text (null if encrypted field failed to decrypt). */
  message: z10.string().nullable(),
  /** Optional message context tag (e.g. 'family', 'work'). */
  context: z10.string().nullable(),
  /** Drama score 0-10. */
  score: z10.number().min(0).max(10),
  /** Drama category — null for circuit-breaker / crisis-safe responses. */
  category: DramaCategoryEnum.nullable(),
  /** Analytical summary (may be null if decryption failed). */
  summary: z10.string().nullable(),
  /** Narrator introduction (tier-dependent; may be null). */
  narrator_intro: z10.string().nullable(),
  /** Detected drama issues — opaque JSON, validated at analyze time. */
  issues: z10.array(z10.unknown()).nullable(),
  /** Suggested rewrites — opaque JSON, validated at analyze time. */
  rewrites: z10.array(z10.unknown()).nullable(),
  /** LLM provider that generated the row. */
  provider: AnalysisProviderEnum.nullable(),
  /** LLM model identifier. */
  model: z10.string().nullable(),
  /** End-to-end pipeline latency for the analysis that produced this row. */
  latency_ms: z10.number().nullable(),
  /** Tokens consumed by the LLM call. */
  tokens_used: z10.number().nullable(),
  /** ISO 8601 timestamp of row creation. */
  created_at: z10.iso.datetime({ offset: true }),
  /** Whether `message` was stored encrypted (handler still returns plaintext post-decrypt). */
  message_encrypted: z10.boolean().nullable().optional(),
  /** Whether `summary` was stored encrypted. */
  summary_encrypted: z10.boolean().nullable().optional(),
  /** Whether `context` was stored encrypted. */
  context_encrypted: z10.boolean().nullable().optional(),
  /** Whether `narrator_intro` was stored encrypted. */
  narrator_intro_encrypted: z10.boolean().nullable().optional(),
  /** Whether `issues` was stored encrypted. */
  issues_encrypted: z10.boolean().nullable().optional(),
  /** Whether `rewrites` was stored encrypted. */
  rewrites_encrypted: z10.boolean().nullable().optional(),
  /** Key version used for encryption (1=shared, 2=per-user). */
  encryption_key_version: z10.number().int().nullable().optional()
});
var AnalysisHistoryPageDto = z10.object({
  /** Rows ordered by `created_at DESC`. */
  entries: z10.array(AnalysisHistoryEntryDto),
  /** Cursor for the next page, or null when the end of the list is reached. */
  nextCursor: z10.string().nullable().default(null)
});

// src/schemas/practitioner.ts
import { z as z11 } from "zod";
var PracticeTypeEnum = z11.enum(["therapist", "coach", "counselor", "other"]);
var CertificationStatusEnum = z11.enum(["certified", "pending", "suspended"]);
var PractitionerStatusEnum = z11.enum([
  "approved",
  "invited",
  "applied",
  "rejected",
  "suspended"
]);
var CrisisContactMethodEnum = z11.enum(["email", "push", "both"]);
var CrisisUrgencyPreferenceEnum = z11.enum(["all", "high_only", "critical_only"]);
var PractitionerProfileDto = z11.object({
  /** Row primary key (`practitioner_profiles.id`). */
  id: z11.uuid(),
  /** Practice type (therapist / coach / counselor / other). */
  practice_type: PracticeTypeEnum,
  /** Display name of the practice — null if not set. */
  practice_name: z11.string().nullable(),
  /** Certification status — defaults to 'pending' for new profiles. */
  certification_status: CertificationStatusEnum,
  /** Whether this practitioner is part of the founding cohort. */
  founding_practitioner: z11.boolean(),
  /** Lifecycle status — only `approved` profiles unlock practitioner UI. */
  status: PractitionerStatusEnum,
  /** Practitioner referral code — null if not yet generated. */
  referral_code: z11.string().nullable()
});
var PractitionerDashboardDto = z11.object({
  /** Profile row primary key. */
  id: z11.uuid(),
  /** Display name of the practice — null if not set. */
  practice_name: z11.string().nullable(),
  /** Practice type. */
  practice_type: PracticeTypeEnum,
  // Future enrichment (NocoDB #2513) — currently absent from the handler
  // response. Encoded as `.optional()` so adding them later is purely
  // additive and does not require a wire-format version bump.
  /** Embedded profile object for richer dashboards. */
  profile: PractitionerProfileDto.optional(),
  /** Aggregated stats (active clients, monthly analyses, etc.). */
  stats: z11.object({
    active_clients: z11.number().int().min(0).optional(),
    active_clients_delta: z11.number().nullable().optional(),
    analyses_this_month: z11.number().int().min(0).optional(),
    analyses_this_month_delta: z11.number().nullable().optional(),
    earnings_this_month: z11.string().optional(),
    clients_improving_pct: z11.number().min(0).max(100).optional()
  }).optional(),
  /** Recent client analyses surfaced on the dashboard. */
  recent_analyses: z11.array(
    z11.object({
      id: z11.string(),
      client_name: z11.string().nullable().optional(),
      category: z11.string().nullable().optional(),
      score: z11.number().nullable().optional(),
      created_at: z11.iso.datetime({ offset: true })
    })
  ).optional()
});
var PractitionerConsentRecordDto = z11.object({
  /** Consent row primary key. */
  id: z11.uuid(),
  /** Target practitioner user ID (Better Auth text ID, not UUID). */
  practitioner_id: BetterAuthUserIdSchema,
  /** Permits the practitioner to view the patient's analysis history. */
  can_view_history: z11.boolean(),
  /** Permits the practitioner to view the original message text. */
  can_view_original_message: z11.boolean(),
  /** Permits the practitioner to chat with the patient in-app. */
  can_chat: z11.boolean(),
  /** Whether the practitioner must approve the patient's analysis results. */
  requires_approval_for_results: z11.boolean(),
  /** Whether the practitioner is opted in to crisis notifications. */
  allow_crisis_notifications: z11.boolean(),
  // Optional fields — present when the handler selects them or when the
  // join with `practitioner_profiles` is enabled.
  /** Patient user ID — only present on enriched views (Better Auth text ID, not UUID). */
  patient_id: BetterAuthUserIdSchema.optional(),
  /** Practitioner group ID — only present on enriched views. */
  practitioner_group_id: z11.uuid().optional(),
  /** Crisis contact method — null when crisis notifications are off. */
  crisis_notification_contact_method: z11.string().nullable().optional(),
  /** Crisis urgency preference — null when crisis notifications are off. */
  crisis_notification_urgency_preference: z11.string().nullable().optional(),
  /** Joined practitioner display name. */
  practice_name: z11.string().nullable().optional(),
  /** Joined practitioner practice type. */
  practice_type: z11.string().nullable().optional(),
  /** Consent row creation timestamp. */
  created_at: z11.iso.datetime({ offset: true }).optional(),
  /** Consent row last update timestamp. */
  updated_at: z11.iso.datetime({ offset: true }).optional()
});
var PractitionerConsentListDto = z11.array(PractitionerConsentRecordDto);
var PractitionerInviteResultDto = z11.object({
  invite_id: z11.string().optional(),
  invite_code: z11.string().optional(),
  deep_link: z11.string().optional(),
  web_link: z11.string().optional(),
  expires_at: z11.iso.datetime({ offset: true }).optional()
}).passthrough();
var PractitionerAcceptResultDto = z11.object({
  practitioner_group_id: z11.string().optional(),
  role: z11.string().optional(),
  joined_at: z11.iso.datetime({ offset: true }).optional()
}).passthrough();
var PractitionerRemoveResultDto = z11.object({
  removed: z11.boolean().optional(),
  practitioner_id: z11.string().optional()
}).passthrough();
var PractitionerConsentRevokeResultDto = z11.object({
  revoked: z11.literal(true)
});
var PractitionerConsentUpdateResultDto = z11.object({
  updated: z11.literal(true)
});
var SessionPrepPatternItemDto = z11.object({
  /** Short label for the pattern (e.g. 'Stonewalling'). */
  pattern: z11.string(),
  /** Detail string — handler clamps to ≤100 chars during normalization. */
  detail: z11.string()
});
var SessionPrepResponseDto = z11.object({
  /** Patterns trending downward across the recent analyses. */
  improving: z11.array(SessionPrepPatternItemDto).max(3),
  /** Patterns persisting at concerning levels. */
  persistent: z11.array(SessionPrepPatternItemDto).max(3),
  /** Patterns newly emerging vs. the prior baseline. */
  new_patterns: z11.array(SessionPrepPatternItemDto).max(3),
  /** Suggested clinical focus topics for the next session. */
  suggested_focus: z11.array(z11.string()).max(3),
  /** ISO 8601 timestamp when the response was generated. */
  generated_at: z11.iso.datetime({ offset: true }),
  /** Number of analyses the LLM was given as input (≤5). */
  analyses_count: z11.number().int().min(0).max(5)
});

// src/schemas/messaging.ts
import { z as z13 } from "zod";

// src/utils/validation.ts
import { z as z12 } from "zod";
var MessageContextSchema = z12.enum([
  "family",
  "work",
  "friends",
  "dating",
  "social",
  "other"
]);
var DramaAnalysisRequestSchema = z12.object({
  message: z12.string().min(1, "Message cannot be empty").max(5e3, "Message too long (max 5000 characters)"),
  context: MessageContextSchema.optional(),
  userId: z12.string().uuid().optional(),
  isPremium: z12.boolean().optional().default(false)
});
function validateMessage(message) {
  if (!message || message.trim().length === 0) {
    return { valid: false, error: "Message cannot be empty" };
  }
  if (message.length > 5e3) {
    return { valid: false, error: "Message too long (max 5000 characters)" };
  }
  if (message.trim().length < 3) {
    return { valid: false, error: "Message too short (min 3 characters)" };
  }
  return { valid: true };
}
function validateContext(context) {
  const result = MessageContextSchema.safeParse(context);
  if (!result.success) {
    return {
      valid: false,
      error: `Invalid context. Must be one of: ${MessageContextSchema.options.join(", ")}`
    };
  }
  return { valid: true };
}
function validateScore(score) {
  if (typeof score !== "number" || isNaN(score)) {
    return { valid: false, error: "Score must be a number" };
  }
  if (score < 0 || score > 10) {
    return { valid: false, error: "Score must be between 0 and 10" };
  }
  return { valid: true };
}
function sanitizeInput(input) {
  return input.trim().replace(/[<>]/g, "").replace(/\s+/g, " ");
}
function sanitizeEmail(email) {
  if (!email) return "";
  return email.trim().toLowerCase();
}
function validateEmail(email) {
  const emailSchema = z12.string().email();
  const result = emailSchema.safeParse(email);
  if (!result.success) {
    return { valid: false, error: "Invalid email format" };
  }
  return { valid: true };
}
function validateAndSanitizeEmail(email) {
  const sanitized = sanitizeEmail(email);
  if (!sanitized) {
    return { valid: false, sanitized: "", error: "Email is required" };
  }
  const validation = validateEmail(sanitized);
  return { ...validation, sanitized };
}
function getEmailDomain(email) {
  const sanitized = sanitizeEmail(email);
  const validation = validateEmail(sanitized);
  if (!validation.valid) return null;
  const parts = sanitized.split("@");
  return parts[1] || null;
}
function isFreeEmailProvider(email) {
  const domain = getEmailDomain(email);
  if (!domain) return false;
  const freeProviders = [
    "gmail.com",
    "yahoo.com",
    "outlook.com",
    "hotmail.com",
    "icloud.com",
    "protonmail.com",
    "aol.com",
    "mail.com",
    "zoho.com"
  ];
  return freeProviders.includes(domain);
}
function validateUuid(uuid) {
  const uuidSchema = z12.string().uuid();
  const result = uuidSchema.safeParse(uuid);
  if (!result.success) {
    return { valid: false, error: "Invalid UUID format" };
  }
  return { valid: true };
}
function isDefined(value) {
  return value !== void 0 && value !== null;
}
function isNonEmptyString(value) {
  return typeof value === "string" && value.trim().length > 0;
}

// src/schemas/messaging.ts
var ConversationStatusEnum = z13.enum(["active", "archived", "deleted"]);
var MessageContextEnum = MessageContextSchema;
var ConversationDto = z13.object({
  /** Conversation primary key (BA nanoid string). */
  id: z13.string(),
  /** Creator / owner user id (BA nanoid). */
  user_id: z13.string(),
  /** Optional human-readable title. */
  title: z13.string().nullable(),
  /** Lifecycle state — 'deleted' rows are filtered out by list endpoints. */
  status: ConversationStatusEnum,
  /** Subscription tier required to access this conversation (e.g. 'drama_plus'). */
  tier_required: z13.string(),
  /** Timestamp of the most recent message; null for empty threads. */
  last_message_at: z13.string().nullable(),
  /** ISO 8601 timestamp of row creation. */
  created_at: z13.string(),
  /** ISO 8601 timestamp of last update. */
  updated_at: z13.string()
});
var CreateConversationResultDto = z13.object({
  conversation: ConversationDto
});
var ConversationLastMessagePreviewDto = z13.object({
  /** Content truncated to the server-side preview length (~120 chars). */
  contentPreview: z13.string(),
  /** Drama score 0..100 (numeric(4,1) coerced to number); null if not yet analyzed. */
  dramaScore: z13.number().nullable(),
  /** ISO 8601 timestamp of the previewed message. */
  createdAt: z13.string()
});
var ConversationListEntryDto = z13.object({
  id: z13.string(),
  title: z13.string().nullable(),
  status: ConversationStatusEnum,
  tierRequired: z13.string(),
  lastMessageAt: z13.string().nullable(),
  createdAt: z13.string(),
  updatedAt: z13.string(),
  /** Count of messages from other users the caller has not read yet. */
  unreadCount: z13.number().int().min(0),
  /** Latest message preview, or null for empty threads. */
  lastMessage: ConversationLastMessagePreviewDto.nullable()
});
var ListConversationsPageDto = z13.object({
  conversations: z13.array(ConversationListEntryDto),
  /**
   * Cursor for the next page. Format is pipe-delimited
   * "last_message_at|created_at" of the last returned row, or null at the
   * end of the list. See `list-conversations.ts` for cursor semantics.
   */
  nextCursor: z13.string().nullable()
});
var MessageDto = z13.object({
  /** Message primary key (BA nanoid string). */
  id: z13.string(),
  /** Parent conversation id. */
  conversationId: z13.string(),
  /** Author user id (BA nanoid). */
  userId: z13.string(),
  /** Message body (1..2000 chars enforced at write time). */
  content: z13.string(),
  /** Drama score 0..100; null while pipeline analysis is in flight. */
  dramaScore: z13.number().nullable(),
  /** Narrator commentary; null while pipeline analysis is in flight. */
  narratorCommentary: z13.string().nullable(),
  /** Detected drama issues — opaque JSON, validated at analyze time. */
  issues: z13.array(z13.unknown()),
  /** Suggested rewrites — opaque JSON, validated at analyze time. */
  suggestedRewrites: z13.array(z13.unknown()),
  /** Optional client-supplied correlation id for optimistic UI reconciliation. */
  clientMessageId: z13.string().nullable().optional(),
  /** ISO 8601 timestamp of row creation. */
  createdAt: z13.string()
});
var GetMessagesPageDto = z13.object({
  messages: z13.array(MessageDto),
  /** True iff the page filled to the requested limit. */
  hasMore: z13.boolean(),
  /**
   * Cursor for the next page (the `created_at` of the last returned message),
   * or null when the result set is empty.
   */
  nextCursor: z13.string().nullable()
});
var SendMessageCompletedDto = z13.object({
  status: z13.literal("complete"),
  message: MessageDto
});
var SendMessagePendingDto = z13.object({
  status: z13.literal("pending"),
  /** Always null for pending — message is not yet persisted. */
  message: z13.null(),
  /** Suggested wait (seconds) before retry. */
  retryAfter: z13.number().int().min(0)
});
var SendMessageResultDto = z13.discriminatedUnion("status", [
  SendMessageCompletedDto,
  SendMessagePendingDto
]);
var MarkMessagesReadResultDto = z13.object({
  markedIds: z13.array(z13.string())
});

// src/schemas/safety.ts
import { z as z14 } from "zod";
var CrisisUrgencyLevelEnum = z14.enum(["standard", "urgent", "critical"]);
var CrisisAssistanceRequestResultDto = z14.object({
  /** UUID of the (created or existing) crisis assistance request row. */
  request_id: z14.uuid(),
  /** 'created' for a new request, 'existing' for duplicate-prevention. */
  status: z14.enum(["created", "existing"]),
  /** ISO 8601 timestamp at which the request expires (72h from creation). */
  expires_at: z14.iso.datetime({ offset: true }),
  /** True when the practitioner was notified on this call. */
  practitioner_notified: z14.boolean()
});
var CrisisAssistanceRespondResultDto = z14.object({
  /** Row primary key — echoes the request being responded to. */
  id: z14.uuid(),
  /** Always 'responded' on success. */
  status: z14.literal("responded"),
  /** Practitioner-supplied response classification. */
  response_type: z14.string(),
  /** Optional notes (may be null when not provided). */
  notes: z14.string().nullable(),
  /** ISO 8601 timestamp at which the practitioner responded. */
  responded_at: z14.iso.datetime({ offset: true }).nullable()
});
var CrisisFeedbackSubmitResultDto = z14.object({
  /** UUID of the inserted crisis_detection_feedback row. */
  id: z14.uuid()
});
var SubmitConcernResultDto = z14.object({
  /** UUID of the inserted user_reports row. */
  report_id: z14.uuid()
});
var QuotaRefundResultDto = z14.object({
  /** UUID of the new ledger entry created for the refund. */
  refundLedgerId: z14.uuid(),
  /** UUID of the original ledger entry being refunded, or null if untracked. */
  originalLedgerId: z14.uuid().nullable(),
  /** Number of units refunded (typically 1). */
  units: z14.number().int().min(0)
});

// src/schemas/admin.ts
import { z as z15 } from "zod";
var PatternTypeDtoEnum = z15.enum([
  "aggressive",
  "passive",
  "assertive",
  "analytical"
]);
var PatternStatusDtoEnum = z15.enum(["draft", "published", "archived"]);
var CommunicationPatternDto = z15.object({
  /** Row primary key. */
  id: z15.uuid(),
  /** Human-readable pattern name (3-100 chars). */
  pattern_name: z15.string(),
  /** Communication style category. */
  pattern_type: PatternTypeDtoEnum,
  /** Optional descriptive blurb for the admin UI. */
  description: z15.string().nullable().optional(),
  /** Text snippets that signal the pattern (1-20 entries, each 1-100 chars). */
  indicators: z15.array(z15.string()),
  /** Author-facing guidance shown when the pattern is matched. */
  guidance_text: z15.string(),
  /** Severity 1-5 (1 = mild, 5 = severe). */
  severity: z15.number().int().min(1).max(5),
  /** Lifecycle state. */
  status: PatternStatusDtoEnum,
  /** True when the pattern is visible to non-admin users. */
  is_public: z15.boolean(),
  /** Admin who authored the pattern (mutually exclusive with user_id). */
  admin_user_id: BetterAuthUserIdSchema.nullable().optional(),
  /** End-user who authored the pattern (mutually exclusive with admin_user_id). */
  user_id: BetterAuthUserIdSchema.nullable().optional(),
  /** ISO 8601 timestamp of row creation. */
  created_at: z15.iso.datetime({ offset: true }).optional(),
  /** ISO 8601 timestamp of the last update. */
  updated_at: z15.iso.datetime({ offset: true }).nullable().optional()
});
var PatternListDto = z15.object({
  /** Page of pattern rows, ordered by `created_at DESC`. */
  patterns: z15.array(CommunicationPatternDto),
  /** Pagination metadata. */
  meta: z15.object({
    /** Total row count matching the filter (across all pages). */
    total: z15.number().int().min(0).nullable(),
    /** Page size requested by the caller. */
    limit: z15.number().int().min(1),
    /** Row offset of the first entry in this page. */
    offset: z15.number().int().min(0)
  })
});
var PatternDetailDto = CommunicationPatternDto;
var PatternCreateResultDto = CommunicationPatternDto;
var PatternUpdateResultDto = CommunicationPatternDto;
var PatternArchiveResultDto = CommunicationPatternDto;
var PatternPublishResultDto = CommunicationPatternDto;

// src/schemas/onboarding.ts
import { z as z16 } from "zod";
var OnboardingStatusDto = z16.object({
  /** Whether the user has completed the onboarding flow. */
  is_onboarded: z16.boolean().nullable(),
  /** ISO 8601 timestamp of the most recent foreground activity, or null. */
  last_seen_at: z16.iso.datetime({ offset: true }).nullable(),
  /** Whether the user has opted into AI training data use, or null. */
  training_consent: z16.boolean().nullable()
});
var UpdateOnboardingResultDto = z16.object({
  /** Always true on success. */
  updated: z16.literal(true)
});
var AcceptTosResultDto = z16.object({
  /** ISO 8601 timestamp when the acceptance row was inserted, or null if duplicate. */
  accepted_at: z16.iso.datetime({ offset: true }).nullable(),
  /** True when a prior acceptance for the same version already existed. */
  already_accepted: z16.boolean()
});
var UpdateLastActiveResultDto = z16.object({
  /** ISO 8601 timestamp the handler wrote to `profiles.last_seen_at`. */
  updated_at: z16.iso.datetime({ offset: true })
});

// src/schemas/preferences.ts
import { z as z17 } from "zod";
var RetentionDaysEnum = z17.union([
  z17.literal(30),
  z17.literal(60),
  z17.literal(90),
  z17.literal(180),
  z17.literal(365)
]);
var UserPreferencesDto = z17.object({
  /** Data retention window in days (30 / 60 / 90 / 180 / 365). */
  retention_days: RetentionDaysEnum
});
var UpdatePreferencesResultDto = z17.object({
  /** Always true on success. */
  updated: z17.literal(true)
});

// src/schemas/data-lifecycle.ts
import { z as z18 } from "zod";
var ClaimAnonymousDataResultDto = z18.object({
  /** Number of anonymous rows transferred to the authenticated user. */
  claimed: z18.number().int().min(0)
});
var ConfirmAnalysisReceiptResultDto = z18.object({
  /** True when the ledger row was already confirmed on a prior call. */
  alreadyConfirmed: z18.boolean()
});
var DeleteUserDataCountsDto = z18.record(z18.string(), z18.number().int().min(0));
var CryptoShreddingInfoDto = z18.object({
  /** Count of per-user-encrypted rows that become unreadable post-delete. */
  records_shredded: z18.number().int().min(0),
  /** Shredding method identifier (informational only). */
  method: z18.string()
});
var DeleteUserDataResultDto = z18.object({
  /** Per-store deletion summary. */
  deleted: z18.object({
    /** Per-table row counts from the SQL cascade. */
    database: DeleteUserDataCountsDto,
    /** Redis cleanup status (always `'completed'` today — non-fatal on error). */
    redis_cleanup: z18.literal("completed")
  }),
  /** Crypto-shredding audit for per-user-encrypted rows. */
  cryptoShredding: CryptoShreddingInfoDto
});
var PartnerTypeEnum = z18.enum([
  "practitioner",
  "coach",
  "influencer",
  "corporate",
  "other"
]);
var PartnerCertificationTypeEnum = z18.enum(["founding", "standard"]);
var PartnerFunnelStepDto = z18.object({
  /** Human-readable label (e.g. 'Invited', 'Signed Up', 'Subscribed'). */
  label: z18.string(),
  /** Absolute count for this step. */
  count: z18.number().int().min(0),
  /** Conversion percentage vs. the previous step, 0-100. */
  percentage: z18.number().min(0).max(100)
});
var PartnerCertificationDto = z18.object({
  /** 'founding' for founding-cohort partners, 'standard' otherwise. */
  type: PartnerCertificationTypeEnum,
  /** Month string in YYYY-MM form (handler: `to_char(..., 'YYYY-MM')`). */
  date: z18.string()
});
var PartnerDashboardResultDto = z18.object({
  /** Partner referral code, empty string when unset. */
  referralCode: z18.string(),
  /** Commission rate as a fraction (0-1), e.g. 0.20 = 20%. */
  commissionRate: z18.number().min(0).max(1),
  /** Founding-cohort flag. */
  foundingPractitioner: z18.boolean(),
  /** Partner classification. */
  partnerType: PartnerTypeEnum,
  /** Ordered funnel (Invited → Signed Up → Subscribed → Active). */
  funnel: z18.array(PartnerFunnelStepDto),
  /** Certification badge summary. */
  certification: PartnerCertificationDto
});

// src/schemas/referral.ts
import { z as z19 } from "zod";
var ReferralStatusEnum = z19.enum([
  "pending",
  "signed_up",
  "subscribed",
  "rewarded",
  "expired"
]);
var ReferralRowDto = z19.object({
  /** Row primary key. */
  id: z19.uuid(),
  /** Email of the invitee, or null when not captured. */
  referred_email: z19.string().nullable(),
  /** Current lifecycle state. */
  status: ReferralStatusEnum,
  /** ISO 8601 timestamp of creation. */
  created_at: z19.iso.datetime({ offset: true })
});
var CustomerReferralDto = z19.object({
  /** The user's active referral code, or null if they haven't generated one. */
  code: z19.string().nullable(),
  /** List of referrals authored by the caller. */
  referrals: z19.array(ReferralRowDto)
});
var ReferralCodeResultDto = z19.object({
  /** The canonical referral code (8-char alphanumeric, unambiguous chars). */
  code: z19.string(),
  /** URL path fragment to share: `/ref/<code>`. */
  path: z19.string(),
  /** True when a new row was inserted; false when the caller already had one. */
  isNew: z19.boolean()
});

// src/schemas/content.ts
import { z as z20 } from "zod";
var CreateSharedLinkResultDto = z20.object({
  /** 32-byte opaque base64url token returned exactly once after publication. */
  link_token: z20.string().regex(/^[A-Za-z0-9_-]{43}$/),
  /**
   * App-relative public URL, e.g. `/s/{link_token}`. Clients prefix with the
   * configured app origin before surfacing to the owner.
   */
  shortUrl: z20.string().startsWith("/s/")
});
var RevokeSharedLinkResultDto = z20.object({
  revoked: z20.literal(true)
});
var E2ESharedLinkFixtureResultDto = z20.object({
  analysis_id: z20.string().uuid()
});
var SharedAnalysisCategoryEnum = z20.enum([
  "safe",
  "caution",
  "high_drama",
  "extreme_drama"
]);
var SharedAnalysisProviderEnum = z20.enum([
  "groq",
  "openai",
  "google",
  "anthropic",
  "safety"
]);
var SharedLinkPrivacySettingsDto = z20.object({
  hide_message: z20.boolean(),
  hide_score: z20.boolean(),
  hide_summary: z20.boolean(),
  hide_narrator: z20.boolean(),
  hide_snippets: z20.boolean(),
  hide_rewrites: z20.boolean()
});
var SharedAnalysisDto = z20.object({
  /** Row primary key (analysis_history.id). */
  id: z20.uuid(),
  /** Echo of `id` — appended by the handler for client compatibility. */
  analysis_id: z20.uuid().nullable(),
  /** Original message text; null when `hide_message` is set OR decryption failed. */
  message: z20.string().nullable(),
  /** Drama score 0-10; null when `hide_score` is set. */
  score: z20.number().min(0).max(10).nullable(),
  /** Drama category; null for circuit-breaker / crisis-safe responses. */
  category: SharedAnalysisCategoryEnum.nullable(),
  /** Analytical summary; null when `hide_summary` is set OR decryption failed. */
  summary: z20.string().nullable(),
  /** Narrator intro; null when `hide_narrator` is set OR creator's tier lacks narrator. */
  narrator_intro: z20.string().nullable().optional(),
  /** Detected drama issues — opaque JSON (snippet fields nulled when `hide_snippets` is set). */
  issues: z20.array(z20.unknown()).nullable(),
  /** Suggested rewrites — opaque JSON (text fields nulled when `hide_rewrites` is set). */
  rewrites: z20.array(z20.unknown()).nullable(),
  /** LLM provider that generated the row. */
  provider: SharedAnalysisProviderEnum.nullable(),
  /** LLM model identifier. */
  model: z20.string().nullable(),
  /** ISO 8601 timestamp of row creation. */
  created_at: z20.iso.datetime({ offset: true }),
  /** Privacy flags applied to this response; null for legacy links with no settings stored. */
  privacy_settings: SharedLinkPrivacySettingsDto.nullable()
}).loose();
var ExplicitSharedResultDto = z20.object({
  link_type: z20.literal("explicit_result"),
  score: z20.number().min(0).max(10).nullable(),
  summary: z20.string().nullable()
});
var PublicSharedAnalysisDto = z20.union([
  SharedAnalysisDto,
  ExplicitSharedResultDto
]);
var AnalysisRevisionStatusEnum = z20.enum([
  "in_progress",
  "completed",
  "abandoned"
]);
var AnalysisRevisionDto = z20.object({
  /** Row primary key. */
  id: z20.uuid(),
  /** Parent analysis this revision mutates. */
  analysis_id: z20.uuid(),
  /** Owner user id (no anonymous revisions — handler is auth-only). */
  user_id: z20.string(),
  /** Lifecycle state. */
  status: AnalysisRevisionStatusEnum,
  /** Original message text captured at revision creation time. */
  original_message: z20.string(),
  /** Original drama score (0-10, but historically stored without clamping). */
  original_score: z20.number(),
  /** Original category string (not enum-narrowed; retained for legacy rows). */
  original_category: z20.string(),
  /** Original detected issues — opaque JSON. */
  original_issues: z20.array(z20.unknown()),
  /** Optional original summary. */
  original_summary: z20.string().nullable().optional(),
  /** Revised message text; populated once complete-revision runs. */
  revised_message: z20.string().nullable().optional(),
  /** Revised score; populated once complete-revision runs. */
  revised_score: z20.number().nullable().optional(),
  /** Revised category; populated once complete-revision runs. */
  revised_category: z20.string().nullable().optional(),
  /** Revised issues — opaque JSON; populated once complete-revision runs. */
  revised_issues: z20.array(z20.unknown()).nullable().optional(),
  /** Revised summary; populated once complete-revision runs. */
  revised_summary: z20.string().nullable().optional(),
  /** Count of issues the user resolved — populated on completion. */
  issues_resolved: z20.number().int().nullable().optional(),
  /** Total issues in the original analysis — stored at creation. */
  issues_total: z20.number().int().nullable().optional(),
  /** Optional agent execution pointer. */
  agent_execution_id: z20.string().nullable().optional(),
  /** Optional LLM model identifier. */
  model_id: z20.string().nullable().optional(),
  /** ISO 8601 timestamp of row creation. */
  created_at: z20.iso.datetime({ offset: true }),
  /** ISO 8601 timestamp of completion; null while in_progress. */
  completed_at: z20.iso.datetime({ offset: true }).nullable().optional()
}).loose();
var RewriteDecisionEnum = z20.enum(["accepted", "ignored", "modified"]);
var RewriteIssueSeverityEnum = z20.enum(["low", "medium", "high"]);
var RewriteEventDto = z20.object({
  /** Row primary key. */
  id: z20.uuid(),
  /** Parent revision row. */
  revision_id: z20.uuid(),
  /** Stable id of the issue this event targets (issue PK from analysis payload). */
  issue_id: z20.string(),
  /** Optional free-text issue type label (passed through from the analysis payload). */
  issue_type: z20.string().nullable().optional(),
  /** Optional severity tag. */
  severity: RewriteIssueSeverityEnum.nullable().optional(),
  /** The snippet of the original message the user was deciding on. */
  snippet: z20.string(),
  /** Suggested replacement text (may be null for "no-op" suggestions). */
  replacement: z20.string().nullable().optional(),
  /** Start index of the snippet in the original message. */
  start_index: z20.number().int().nullable().optional(),
  /** End index of the snippet in the original message. */
  end_index: z20.number().int().nullable().optional(),
  /** User decision. */
  decision: RewriteDecisionEnum,
  /** When decision=modified, the text the user actually typed. */
  user_modified_text: z20.string().nullable().optional(),
  /** Decision latency in milliseconds (from suggestion render to click). */
  decision_latency_ms: z20.number().int().nullable().optional(),
  /** Suggestion metadata (agent/provider/model/tokens/cost). */
  suggestion_agent_id: z20.string().nullable().optional(),
  suggestion_provider: z20.string().nullable().optional(),
  suggestion_model: z20.string().nullable().optional(),
  suggestion_tokens: z20.number().int().nullable().optional(),
  suggestion_cost_usd: z20.number().nullable().optional(),
  /** ISO 8601 timestamp of row creation. */
  created_at: z20.iso.datetime({ offset: true })
}).loose();
var AgentExecutionStatusEnum = z20.enum([
  "pending",
  "running",
  "completed",
  "failed",
  "cancelled"
]);
var AgentExecutionEntryDto = z20.object({
  /** Row primary key. */
  id: z20.uuid(),
  /** Agent identifier (e.g. 'sharecopy_generator', 'tooltip_writer'). */
  agent_name: z20.string(),
  /** Raw agent output JSON (opaque at the wire layer). */
  output: z20.unknown().nullable(),
  /** Execution status — narrowed at read time; the DTO widens to string for forward compat. */
  status: z20.string(),
  /** ISO 8601 timestamp of row creation. */
  created_at: z20.iso.datetime({ offset: true })
}).loose();
var GeneratedContentResultDto = z20.array(AgentExecutionEntryDto);

// src/schemas/feedback.ts
import { z as z21 } from "zod";
var FeedbackTypeEnum = z21.enum(["positive", "negative"]);
var AnalysisFeedbackSourceEnum = z21.enum([
  "analysis_result",
  "shared_link",
  "history_detail",
  "rewrite_suggestion",
  "other"
]);
var FeedbackSubmitResultDto = z21.object({
  /** Row id of the inserted `analysis_feedback` record. */
  feedback_id: z21.uuid()
});
var FeedbackEntryDto = z21.object({
  /** Row primary key. */
  id: z21.uuid(),
  /** Thumbs-up / thumbs-down sentiment. */
  type: FeedbackTypeEnum,
  /** UI surface the feedback was submitted from. */
  source: AnalysisFeedbackSourceEnum,
  /** Optional free-form comment (up to analyze-DB column limit). */
  comment: z21.string().nullable(),
  /** ISO 8601 timestamp of row creation. */
  created_at: z21.iso.datetime({ offset: true })
});
var GetFeedbackResultDto = z21.object({
  feedback: FeedbackEntryDto.nullable()
});
var UserStatsRowDto = z21.object({
  /** Owner user id. */
  user_id: z21.string(),
  /** Analyses consumed in the current daily window. */
  daily_analyses_used: z21.number().int().min(0),
  /** ISO 8601 timestamp at which the daily counter resets. */
  daily_reset_at: z21.iso.datetime({ offset: true })
}).loose();
var UserStatsDto = UserStatsRowDto.nullable();

export {
  MessageContextSchema,
  DramaAnalysisRequestSchema,
  validateMessage,
  validateContext,
  validateScore,
  sanitizeInput,
  sanitizeEmail,
  validateEmail,
  validateAndSanitizeEmail,
  getEmailDomain,
  isFreeEmailProvider,
  validateUuid,
  isDefined,
  isNonEmptyString,
  QuotaUsageDto,
  AnonymousQuotaDto,
  FamilyQuotaDto,
  QuotaPolicyDto,
  EntitlementsDto,
  TierIdEnum,
  VisualLayerEnum,
  TierSourceEnum,
  TierDto,
  FamilyPlanDetailsDto,
  FamilyMemberDto,
  FamilyInviteDto,
  FamilyPlanDto,
  FamilyPlanTierDtoEnum,
  FamilyInviteBillingIntervalEnum,
  FamilyInviteStatusEnum,
  FamilyInviteDetailsDto,
  FamilyInviteResultDto,
  FamilyAcceptResultDto,
  FamilyRemoveResultDto,
  FamilyCancelInviteResultDto,
  SubscriptionTierEnum,
  SubscriptionStatusEnum,
  SubscriptionStatusDto,
  CancelSubscriptionResultDto,
  UpdateSubscriptionActionEnum,
  UpdateSubscriptionResultDto,
  CustomerPortalSessionResultDto,
  AnalysisStatsDto,
  AnalysisRecordCategoryEnum,
  AnalysisRecordProviderEnum,
  AnalysisDto,
  DeleteAnalysisResultDto,
  ClearAnalysisResultDto,
  DramaCategoryEnum,
  AnalysisProviderEnum,
  AnalysisHistoryEntryDto,
  AnalysisHistoryPageDto,
  PracticeTypeEnum,
  CertificationStatusEnum,
  PractitionerStatusEnum,
  CrisisContactMethodEnum,
  CrisisUrgencyPreferenceEnum,
  PractitionerProfileDto,
  PractitionerDashboardDto,
  PractitionerConsentRecordDto,
  PractitionerConsentListDto,
  PractitionerInviteResultDto,
  PractitionerAcceptResultDto,
  PractitionerRemoveResultDto,
  PractitionerConsentRevokeResultDto,
  PractitionerConsentUpdateResultDto,
  SessionPrepPatternItemDto,
  SessionPrepResponseDto,
  ConversationStatusEnum,
  MessageContextEnum,
  ConversationDto,
  CreateConversationResultDto,
  ConversationLastMessagePreviewDto,
  ConversationListEntryDto,
  ListConversationsPageDto,
  MessageDto,
  GetMessagesPageDto,
  SendMessageCompletedDto,
  SendMessagePendingDto,
  SendMessageResultDto,
  MarkMessagesReadResultDto,
  CrisisUrgencyLevelEnum,
  CrisisAssistanceRequestResultDto,
  CrisisAssistanceRespondResultDto,
  CrisisFeedbackSubmitResultDto,
  SubmitConcernResultDto,
  QuotaRefundResultDto,
  PatternTypeDtoEnum,
  PatternStatusDtoEnum,
  CommunicationPatternDto,
  PatternListDto,
  PatternDetailDto,
  PatternCreateResultDto,
  PatternUpdateResultDto,
  PatternArchiveResultDto,
  PatternPublishResultDto,
  OnboardingStatusDto,
  UpdateOnboardingResultDto,
  AcceptTosResultDto,
  UpdateLastActiveResultDto,
  RetentionDaysEnum,
  UserPreferencesDto,
  UpdatePreferencesResultDto,
  ClaimAnonymousDataResultDto,
  ConfirmAnalysisReceiptResultDto,
  DeleteUserDataCountsDto,
  CryptoShreddingInfoDto,
  DeleteUserDataResultDto,
  PartnerTypeEnum,
  PartnerCertificationTypeEnum,
  PartnerFunnelStepDto,
  PartnerCertificationDto,
  PartnerDashboardResultDto,
  ReferralStatusEnum,
  ReferralRowDto,
  CustomerReferralDto,
  ReferralCodeResultDto,
  CreateSharedLinkResultDto,
  RevokeSharedLinkResultDto,
  E2ESharedLinkFixtureResultDto,
  SharedLinkPrivacySettingsDto,
  SharedAnalysisDto,
  ExplicitSharedResultDto,
  PublicSharedAnalysisDto,
  AnalysisRevisionStatusEnum,
  AnalysisRevisionDto,
  RewriteDecisionEnum,
  RewriteIssueSeverityEnum,
  RewriteEventDto,
  AgentExecutionStatusEnum,
  AgentExecutionEntryDto,
  GeneratedContentResultDto,
  FeedbackTypeEnum,
  AnalysisFeedbackSourceEnum,
  FeedbackSubmitResultDto,
  FeedbackEntryDto,
  GetFeedbackResultDto,
  UserStatsRowDto,
  UserStatsDto
};
